import { ActionIcon, Avatar, Burger, Group, Stack, Text, Title, Tooltip } from '@mantine/core';
import { PropsWithChildren, createContext, useContext } from 'react';
import classes from './DrawerScroll.module.css';
import { Link, useLocation } from 'react-router-dom';
import { darkAccent } from '@/res/costants/colors';
import { IconLogout, IconSettings } from '@tabler/icons-react';
import { useSmallerView } from '@/hooks/useSmallerView';
import { t } from 'i18next';
import { useBodyScroll } from '@/hooks/useBodyScroll';
import { Drawer as MantineDrawer } from '@mantine/core';

interface DrawerContextProps {
  selected?: string;
  onNavigate?: (url: string) => void;
  onClose: () => void;
}

const DrawerContext = createContext<DrawerContextProps>({
  onClose: () => {}
});

interface DrawerProps {
  opened: boolean;
  selected?: string;
  onClose: () => void;
  user?: {
    name: string;
    surname: string;
    subHeading?: string;
    image?: string;
  };
  onExit: () => void;
  onNavigate?: DrawerContextProps['onNavigate'];
}

export const Drawer = ({
  children,
  opened,
  user,
  onNavigate,
  onClose,
  onExit
}: PropsWithChildren<DrawerProps>) => {
  const location = useLocation();
  const selected = location.pathname.slice(1).replace(/\d+\??$/, '');
  const isMobile = useSmallerView('xs');

  useBodyScroll(opened);

  return (
    <>
      <MantineDrawer
        size={isMobile ? '100%' : 'sm'}
        zIndex={1005}
        withCloseButton={false}
        padding={0}
        opened={opened}
        onClose={onClose}>
        <Stack
          gap={0}
          className={classes.drawer}
          h="100dvh"
          bg="var(--grayDarkest)"
          style={{
            overflowY: 'auto'
          }}>
          <Stack px="lg" py="sm" bg="accent.4">
            <Group justify="space-between">
              <Burger opened size="sm" color="white" onClick={onClose} />
              <Group>
                <Group wrap="nowrap">
                  <Tooltip
                    zIndex={1006}
                    position="bottom"
                    withArrow
                    label={t`drawer.industry.menuItems.settings`}>
                    <ActionIcon
                      size="md"
                      component={Link}
                      to="/settings"
                      onClick={() => onNavigate?.('/settings')}>
                      <IconSettings color="white" />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip
                    zIndex={1006}
                    position="bottom"
                    withArrow
                    label={t`drawer.common.action`}>
                    <ActionIcon onClick={onExit} size="md" color="white">
                      <IconLogout />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Group>
            {user && (
              <Group wrap="nowrap">
                <Group wrap="nowrap" gap="lg">
                  <Avatar
                    radius="xl"
                    src={user?.image}
                    size="lg"
                    style={{ border: `3px solid ${darkAccent}` }}
                  />
                  <Stack gap={4}>
                    <Title order={4} style={{ color: 'white' }}>
                      {user.name} {user.surname}
                    </Title>
                    {user.subHeading && (
                      <Text size="sm" inline c="white" opacity={0.6}>
                        {user.subHeading}
                      </Text>
                    )}
                  </Stack>
                </Group>
              </Group>
            )}
          </Stack>
          <Stack gap={0}>
            <DrawerContext.Provider value={{ onNavigate, onClose, selected }}>
              {children}
            </DrawerContext.Provider>
          </Stack>
        </Stack>
      </MantineDrawer>
    </>
  );
};

export const useDrawer = () => useContext(DrawerContext);
